// Canyon Storm — Rulebringers variant.
// Rules are documented in docs/plans/005-cs-plan.md § Rulebringers Planning Model.

import type { CSPlanConfig, NonVirusSlot, PhaseDef } from './types';

const meta = {
  key: 'rulebringers' as const,
  name: 'Rulebringers',
  shortDescription:
    'Standard FoA planning model. Strongest 2 = Flex on Virus until Phase 4; ' +
    'Power Tower runs a 4-player core; Warehouses are sacrificed first.',
  integratedAt: '2026-07-01',
  rulebookRef: 'docs/plans/005-cs-plan.md',
  highlights: [
    'Strength = M1 (March 1) power. THP only as tie-breaker.',
    'Flex = 2 strongest players; sit on Virus P1–P3, join the collapse in P4.',
    'Power Tower core = 4 strongest non-flex; move as a unit into Virus in P4.',
    'Data Centres shrink 3 → 2 at Phase 3; Warehouses shrink 2 → 1 at Phase 2 and are abandoned in Phase 4.',
    "Substitutes inherit a starter's full route — no independent building slots. PT core & Flex never subbed.",
  ],
};

// Home layout by strength rank. Rank 0 = strongest non-flex (M1). Total = 18
// (plus 2 flex on Virus = 20). Data Centres / Defense Systems interleaved so
// the two halves get comparable strength.
const homeByRank: NonVirusSlot[] = [
  'powerTower',      // 0 — PT core
  'powerTower',      // 1
  'powerTower',      // 2
  'powerTower',      // 3
  'dataCenter1',     // 4
  'dataCenter2',     // 5
  'defenseSystem1',  // 6
  'defenseSystem2',  // 7
  'dataCenter1',     // 8
  'dataCenter2',     // 9
  'defenseSystem1',  // 10
  'defenseSystem2',  // 11
  'serumFactory1',   // 12
  'serumFactory2',   // 13
  'warehouse1',      // 14
  'warehouse2',      // 15
  'warehouse3',      // 16
  'warehouse4',      // 17
];

const ptCoreRanks = [0, 1, 2, 3] as const;

const phases: PhaseDef[] = [
  {
    title: 'PHASE 1',
    time: '0:00',
    timeColor: 'text-green-400',
    subtitle: 'START – DATA CENTERS OPEN',
    subtitleColor: 'text-yellow-400',
    priorities: {
      dataCenter1: 'high',
      dataCenter2: 'high',
      powerTower: 'critical',
      serumFactory1: null,
      serumFactory2: null,
      defenseSystem1: null,
      defenseSystem2: null,
      virusLab: 'critical',
      warehouse1: 'lower',
      warehouse2: 'lower',
      warehouse3: 'lower',
      warehouse4: 'lower',
    },
    capacities: {
      dataCenter1: 3,
      dataCenter2: 3,
      powerTower: 4,
      serumFactory1: 0,
      serumFactory2: 0,
      defenseSystem1: 0,
      defenseSystem2: 0,
      virusLab: 2,
      warehouse1: 2,
      warehouse2: 2,
      warehouse3: 2,
      warehouse4: 2,
    },
  },
  {
    title: 'PHASE 2',
    time: '5:00',
    timeColor: 'text-yellow-400',
    subtitle: 'DEFENSE SYSTEMS OPEN',
    subtitleColor: 'text-yellow-400',
    priorities: {
      dataCenter1: 'high',
      dataCenter2: 'high',
      powerTower: 'critical',
      serumFactory1: null,
      serumFactory2: null,
      defenseSystem1: 'high',
      defenseSystem2: 'high',
      virusLab: 'critical',
      warehouse1: 'lower',
      warehouse2: 'lower',
      warehouse3: 'lower',
      warehouse4: 'lower',
    },
    capacities: {
      dataCenter1: 3,
      dataCenter2: 3,
      powerTower: 4,
      serumFactory1: 0,
      serumFactory2: 0,
      defenseSystem1: 2,
      defenseSystem2: 2,
      virusLab: 2,
      warehouse1: 1,
      warehouse2: 1,
      warehouse3: 1,
      warehouse4: 1,
    },
  },
  {
    title: 'PHASE 3',
    time: '8:00',
    timeColor: 'text-orange-400',
    subtitle: 'SERUM FACTORIES OPEN',
    subtitleColor: 'text-orange-400',
    priorities: {
      dataCenter1: 'high',
      dataCenter2: 'high',
      powerTower: 'critical',
      serumFactory1: 'medium',
      serumFactory2: 'medium',
      defenseSystem1: 'high',
      defenseSystem2: 'high',
      virusLab: 'critical',
      warehouse1: 'lower',
      warehouse2: 'lower',
      warehouse3: 'lower',
      warehouse4: 'lower',
    },
    capacities: {
      dataCenter1: 2,
      dataCenter2: 2,
      powerTower: 4,
      serumFactory1: 1,
      serumFactory2: 1,
      defenseSystem1: 2,
      defenseSystem2: 2,
      virusLab: 2,
      warehouse1: 1,
      warehouse2: 1,
      warehouse3: 1,
      warehouse4: 1,
    },
  },
  {
    title: 'PHASE 4',
    time: '12:00',
    timeColor: 'text-red-500',
    subtitle: 'VIRUS LAB OPENS – COLLAPSE & WIN',
    subtitleColor: 'text-red-500',
    priorities: {
      dataCenter1: 'high',
      dataCenter2: 'high',
      powerTower: 'critical',
      serumFactory1: 'medium',
      serumFactory2: 'medium',
      defenseSystem1: 'high',
      defenseSystem2: 'high',
      virusLab: 'critical',
      warehouse1: 'lower',
      warehouse2: 'lower',
      warehouse3: 'lower',
      warehouse4: 'lower',
    },
    capacities: {
      dataCenter1: 2,
      dataCenter2: 2,
      powerTower: 4,
      serumFactory1: 1,
      serumFactory2: 1,
      defenseSystem1: 2,
      defenseSystem2: 2,
      virusLab: 6,
      warehouse1: 0,
      warehouse2: 0,
      warehouse3: 0,
      warehouse4: 0,
    },
    // P4 collapse: 6 strongest players (M1) to Virus Lab, next 4 to Power Tower.
    // Sum = 20: VL(6) + PT(4) + DC(2+2) + SF(1+1) + DS(2+2) + WH(0).
    strengthClaims: [
      { slot: 'virusLab', count: 6 },
      { slot: 'powerTower', count: 4 },
    ],
  },
];

export const rulebringersConfig: CSPlanConfig = {
  meta,
  phases,
  homeByRank,
  ptCoreRanks,
};
